"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _css = require("@emotion/css");
var _masthead = _interopRequireDefault(require("@amzn/meridian-tokens/component/masthead"));
var _theme = require("../theme");
var _focus = _interopRequireDefault(require("../_focus"));
var _clickable = _interopRequireDefault(require("../_clickable"));
var _token = require("./../_utils/token");
var _focusRing = _interopRequireDefault(require("./../_styles/focus-ring"));
var _filterPropsByPrefix = _interopRequireDefault(require("./../_utils/filter-props-by-prefix"));
var styles = function styles(t, _ref) {
  var focus = _ref.focus,
    disabled = _ref.disabled;
  return (0, _css.css)({
    display: "flex",
    alignItems: "center",
    color: t("foregroundColor", disabled ? "disabled" : "default"),
    padding: "0 ".concat(t("spacingInsetHorizontal"), "px"),
    alignSelf: "stretch",
    whiteSpace: "nowrap",
    backgroundColor: t("backgroundColorDefault"),
    transition: t.transitionCss("motion", ["color", "background-color"]),
    ":hover": !disabled ? {
      backgroundColor: t("backgroundColorHover")
    } : undefined,
    ":active": !disabled ? {
      backgroundColor: t("backgroundColorPressed")
    } : undefined,
    // HACK: This transform ensures that Chrome renders the text in the link
    // correctly after it has been pushed left or right by the masthead menu
    // button animation. Without this transform the kerning in Chrome ends up
    // a bit off after being moved.
    transform: "translateZ(0)"
  }, (0, _focusRing["default"])(t.tokens, {
    focus: focus
  }));
};
var indicatorStyles = function indicatorStyles(t, _ref2) {
  var selected = _ref2.selected,
    size = _ref2.size;
  return (0, _css.css)({
    position: "relative",
    "&:after": {
      content: "''",
      position: "absolute",
      top: "calc(100% + ".concat(t("indicatorSpacingVertical", size), "px)"),
      right: t("indicatorSpacingHorizontal") - t("spacingInsetHorizontal"),
      left: t("indicatorSpacingHorizontal") - t("spacingInsetHorizontal"),
      height: t("indicatorHeight"),
      backgroundColor: selected ? t("indicatorColor") : "transparent",
      borderRadius: t("indicatorBorderRadius"),
      transition: t.transitionCss("motion", "background-color")
    }
  });
};

/**
 * This component can be used to render a navigational link. Place this as a
 * child of the Masthead component.
 */
var MastheadLink = /*#__PURE__*/_react["default"].forwardRef(function (props, ref) {
  var passThroughProps = (0, _filterPropsByPrefix["default"])(props, ["data-", "aria-"]);
  return /*#__PURE__*/_react["default"].createElement(_theme.Style, {
    tokens: _masthead["default"],
    map: (0, _token.getTokenHelper)("mastheadLink")
  }, function (t) {
    return /*#__PURE__*/_react["default"].createElement(_focus["default"], null, function (_ref3) {
      var focus = _ref3.focus,
        focusProps = _ref3.focusProps,
        focusTrigger = _ref3.focusTrigger;
      return /*#__PURE__*/_react["default"].createElement(_clickable["default"], (0, _extends2["default"])({}, passThroughProps, focusProps, {
        className: styles(t, {
          focus: focus && focusTrigger === "keyboard",
          disabled: props.disabled
        }),
        href: props.href,
        onClick: props.onClick,
        disabled: props.disabled,
        target: props.target,
        rel: props.rel,
        "aria-current": props.selected ? "page" : undefined,
        ref: ref
      }), /*#__PURE__*/_react["default"].createElement("div", {
        className: indicatorStyles(t, props)
      }, props.children));
    });
  });
});
MastheadLink.displayName = "MastheadLink";
MastheadLink.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The link text.
   */
  children: _propTypes["default"].node.isRequired,
  /**
   * All props prefixed with `aria-` are accepted. This is useful for adding [aria
   * attributes](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA) for improved accessibility.
   *
   * Within the MastheadLink component, there are some aria- attributes that we set for ourselves,
   * in order to make sure the component is wired up in a way that makes it accessible
   * right out of the box. If you attempt to override the following property,
   * they'll be ignored:
   *
   * aria-current
   */
  "aria-*": _propTypes["default"].string,
  /**
   * All props starting with `data-` are accepted. This is useful for
   * integrating with other libraries such as Amplify Analytics or Cypress.
   */
  "data-*": _propTypes["default"].string,
  /**
   * This disables interaction with the component and applies special visual
   * styles to indicate that it's not interactive.
   */
  disabled: _propTypes["default"].bool,
  /**
   * If a URL is provided here, the component will behave like a link.
   *
   * Note that you must either set this prop or the `onClick` prop (or both).
   */
  href: _propTypes["default"].string,
  /**
   * The relationship of the linked URL to the current page. Only applicable if
   * `href` is set.
   *
   * See docs for the [a tag's rel attribute](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-rel) for details.
   */
  rel: _propTypes["default"].string,
  /**
   * Use this to mark the link as selected. A link is usually considered
   * selected if the page it references is currently loaded.
   */
  selected: _propTypes["default"].bool,
  /**
   * This is provided by the Masthead component, any value passed here
   * directly will be ignored.
   */
  size: _propTypes["default"].oneOf(["small", "medium", "large"]),
  /**
   * Determine where the linked URL is displayed. This is only applicable if
   * `href` is provided but `onClick` is not.
   *
   * IMPORTANT: If you set this prop to `"_blank"`, make sure to set `rel` to
   * `"noopener noreferrer"` to avoid performance and security issues.
   *
   * See docs for the [a tag's target attribute](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target) for details.
   */
  target: _propTypes["default"].string,
  /**
   * This will be triggered when the component is clicked. If the `href` prop is
   * set, that value will be passed as the first argument to this function.
   *
   * If this prop is used along with the `href` prop, the default link behavior
   * of the component will be disabled and the click handler will be responsible
   * for implementing the behavior of the component.
   *
   * These features make it easy to implement accessible client-side navigation.
   * If you have a client-side navigation function of the form `navigate(<url>)`, you
   * can integrate it into this component by setting a URL via `href` and then
   * passing `navigate` directly to this prop (i.e. `href={url} onClick={navigate}`).
   * See the [Routing and navigation guide](https://meridian.a2z.com/for-developers/guides/routing-and-navigation/?platform=reactjs)
   * for details.
   *
   * Note that you must set either this prop or the `href` prop (or both).
   */
  onClick: _propTypes["default"].func
} : {};
var _default = MastheadLink;
exports["default"] = _default;